import type { CosmosData } from "../parser/types.ts";
import type { NodeRT, SceneGraph } from "./types.ts";
import { LAYOUT } from "../config/cosmos.config.ts";

const TWO = Math.PI * 2;
function gcd(a: number, b: number): number {
  return b ? gcd(b, a % b) : a;
}
// PRNG determinístico (layout estável entre reloads)
function makeRand(seed: number) {
  let s = seed;
  return () => ((s = (s * 16807) % 2147483647) / 2147483647);
}

function ringRadius(depth: number): number {
  const r = LAYOUT.ringRadius;
  return r[Math.min(depth, r.length - 1)];
}

/**
 * Layout hierárquico "sistema solar": cada nó orbita o pai.
 * Tamanho = nível arquitetural (vem do parser); posição = árvore radial.
 * Clusters de topo distribuídos por passo coprimo (massa equilibrada).
 */
export function computeLayout(data: CosmosData): SceneGraph {
  const rt: NodeRT[] = data.nodes.map((n) => ({
    ...n,
    w: 1, depth: 0, ang: 0, bx: 0, by: 0, seed: 0, bspeed: 0.5,
    soft: 1, x: 0, y: 0, hl: 0, sr: 0,
  }));
  const byId = new Map<string, NodeRT>();
  rt.forEach((n) => byId.set(n.id, n));
  const kids = new Map<string, NodeRT[]>();
  rt.forEach((n) => {
    if (n.parent) {
      const arr = kids.get(n.parent) ?? [];
      arr.push(n);
      kids.set(n.parent, arr);
    }
  });
  const root = rt.find((n) => n.parent === null);
  if (!root) throw new Error("[layout] nó raiz (council) ausente");

  // pesos de subárvore
  const weight = (id: string): number => {
    const k = kids.get(id);
    const n = byId.get(id)!;
    if (!k || !k.length) return (n.w = 1);
    let s = 0;
    for (const c of k) s += weight(c.id);
    return (n.w = s);
  };
  weight(root.id);

  const rand = makeRand(987654321);
  const place = (id: string, dir: number, depth: number) => {
    let k = kids.get(id);
    if (!k || !k.length) return;
    const n = byId.get(id)!;
    k = [...k].sort((a, b) => b.w - a.w);
    if (depth === 0) {
      const m = k.length;
      let step = Math.max(1, Math.round(m * 0.5));
      while (step > 1 && gcd(step, m) !== 1) step--;
      if (gcd(step, m) !== 1) step = 1;
      const order = new Array<NodeRT>(m);
      for (let i = 0; i < m; i++) order[(i * step) % m] = k[i];
      k = order;
    }
    const R = ringRadius(depth + 1);
    let tw = 0;
    for (const c of k) tw += Math.sqrt(c.w);
    const fan =
      depth === 0 ? TWO : Math.min(LAYOUT.fanMax, LAYOUT.fanBase + k.length * LAYOUT.fanPerChild);
    let a = depth === 0 ? -Math.PI : dir - fan / 2;
    for (const c of k) {
      const frac = depth === 0 ? 1 / k.length : Math.sqrt(c.w) / tw;
      const mid = a + (fan * frac) / 2;
      c.ang = mid;
      c.depth = depth + 1;
      c.seed = rand() * TWO;
      c.bspeed = 0.5 + rand() * 0.7;
      c.soft = c.depth <= 2 ? 1 : Math.max(0.42, 1 - (c.depth - 2) * 0.17);
      c.bx = n.bx + Math.cos(mid) * R;
      c.by = n.by + Math.sin(mid) * R;
      place(c.id, mid, depth + 1);
      a += fan * frac;
    }
  };
  root.bx = 0; root.by = 0; root.depth = 0; root.ang = 0; root.seed = 0; root.soft = 1;
  place(root.id, 0, 0);

  // raio da subárvore (enquadramento no foco)
  const subR = (id: string): number => {
    const n = byId.get(id)!;
    const k = kids.get(id);
    let m = n.r * 2;
    if (k) for (const c of k) {
      const d = Math.hypot(c.bx - n.bx, c.by - n.by) + subR(c.id);
      if (d > m) m = d;
    }
    return (n.sr = m);
  };
  subR(root.id);

  rt.forEach((n) => { n.x = n.bx; n.y = n.by; });
  return { rt, byId, kids, root };
}
